import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LOGO_URL } from "@/data/content";
import { useT, useRoute } from "@/i18n";
import LanguageSwitcher from "@/components/site/LanguageSwitcher";

export default function Navbar() {
  const t = useT();
  const route = useRoute();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLinks = [
    { label: t.nav.home, to: route("home"), key: "home" },
    { label: t.nav.exhibitions, to: route("exhibitions"), key: "exhibitions" },
    { label: t.nav.venues, to: route("venues"), key: "venues" },
    { label: t.nav.services, to: route("services"), key: "services" },
    { label: t.nav.concepts, to: route("projects"), key: "concepts" },
    { label: t.nav.shop, to: "https://artful-merchandise.com", external: true, key: "shop" },
  ];

  const closeMenu = () => setOpen(false);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md border-b border-black/5"
          : "bg-white/80 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 h-[80px] flex items-center justify-between">
        <Link
          to={route("home")}
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
          aria-label="Artful Contemporary home"
        >
          <img
            src={LOGO_URL}
            alt="Artful"
            className="h-14 md:h-[68px] w-auto select-none"
            draggable="false"
          />
          <span className="hidden sm:inline-block text-[17px] font-bold uppercase tracking-[0.3em] text-black/80 mt-1 border-l border-black/15 pl-3">
            Contemporary
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {navLinks.map((l) =>
            l.external ? (
              <a
                key={l.key}
                data-testid={`nav-${l.key}`}
                href={l.to}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[12px] font-bold uppercase tracking-[0.18em] text-black hover:text-[#C20E1A] transition-colors px-4 py-2"
              >
                {l.label}
              </a>
            ) : (
              <NavLink
                key={l.key}
                to={l.to}
                end={l.key === "home"}
                data-testid={`nav-${l.key}`}
                className={({ isActive }) =>
                  `text-[12px] font-bold uppercase tracking-[0.18em] transition-colors px-4 py-2 ${
                    isActive ? "text-[#C20E1A]" : "text-black hover:text-[#C20E1A]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            )
          )}
          <Link
            to={route("contact")}
            data-testid="nav-contact-cta"
            className="ml-3 bg-[#C20E1A] text-white px-5 py-3 text-[11px] uppercase tracking-[0.18em] font-bold hover:bg-black transition-colors duration-500"
          >
            {t.nav.contact}
          </Link>
          <div className="ml-3 pl-3 border-l border-black/10">
            <LanguageSwitcher tone="dark" />
          </div>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden p-2 -mr-2 text-black"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden bg-white border-t border-black/5 transition-[max-height] duration-500 ${
          open ? "max-h-[640px]" : "max-h-0"
        }`}
      >
        <nav className="flex flex-col px-6 py-6 gap-1">
          {navLinks.map((l) =>
            l.external ? (
              <a
                key={l.key}
                href={l.to}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`mob-nav-${l.key}`}
                onClick={closeMenu}
                className="text-sm font-bold uppercase tracking-[0.18em] py-3 border-b border-black/10 hover:text-[#C20E1A]"
              >
                {l.label}
              </a>
            ) : (
              <NavLink
                key={l.key}
                to={l.to}
                end={l.key === "home"}
                data-testid={`mob-nav-${l.key}`}
                onClick={closeMenu}
                className={({ isActive }) =>
                  `text-sm font-bold uppercase tracking-[0.18em] py-3 border-b border-black/10 ${
                    isActive ? "text-[#C20E1A]" : "hover:text-[#C20E1A]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            )
          )}
          <div className="pt-4">
            <LanguageSwitcher tone="dark" />
          </div>
          <Link
            to={route("contact")}
            data-testid="mob-nav-contact-cta"
            onClick={closeMenu}
            className="mt-4 bg-[#C20E1A] text-white text-center px-6 py-3 text-sm uppercase tracking-[0.18em] font-bold"
          >
            {t.nav.contact}
          </Link>
        </nav>
      </div>
    </header>
  );
}
